// ligfrontend/src/components/common/PhotoUpload.jsx
import React, { useState, useRef } from "react";
import "./PhotoUpload.css";

const PhotoUpload = ({ currentPhoto, onPhotoSelect, onPhotoRemove }) => {
  const [preview, setPreview] = useState(currentPhoto || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Compress image before upload
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with quality setting
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create new file with compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Canvas to Blob conversion failed"));
              }
            },
            "image/jpeg",
            quality
          );
        };

        img.onerror = () => reject(new Error("Image load failed"));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error("FileReader failed"));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Initial file size check (before compression)
    const maxSizeBeforeCompression = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeBeforeCompression) {
      alert("Image is too large. Please select an image smaller than 10MB");
      return;
    }

    try {
      setUploading(true);

      let compressedFile = file;
      let quality = 0.7;
      let maxWidth = 1200;

      // Compress the image
      compressedFile = await compressImage(file, maxWidth, quality);

      console.log(`Original size: ${(file.size / 1024).toFixed(2)}KB`);
      console.log(
        `Compressed size: ${(compressedFile.size / 1024).toFixed(2)}KB`
      );

      // If still too large, try more aggressive compression
      const maxSize = 1024 * 1024; // 1MB
      if (compressedFile.size > maxSize) {
        console.log("File still too large, applying more compression...");

        // Try with smaller width and lower quality
        maxWidth = 800;
        quality = 0.6;
        compressedFile = await compressImage(file, maxWidth, quality);

        console.log(
          `Re-compressed size: ${(compressedFile.size / 1024).toFixed(2)}KB`
        );

        // If STILL too large, try even more aggressive
        if (compressedFile.size > maxSize) {
          maxWidth = 600;
          quality = 0.5;
          compressedFile = await compressImage(file, maxWidth, quality);

          console.log(
            `Final compressed size: ${(compressedFile.size / 1024).toFixed(
              2
            )}KB`
          );
        }
      }

      // Final size check after all compression attempts
      if (compressedFile.size > maxSize) {
        const sizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
        alert(
          `Image is still too large (${sizeMB}MB) after compression. Please select a smaller image or reduce the image quality/resolution before uploading.`
        );
        setUploading(false);
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);

      // Pass compressed file to parent component
      await onPhotoSelect(compressedFile);
    } catch (error) {
      console.error("Error processing photo:", error);
      alert("Failed to process photo. Please try again.");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (onPhotoRemove) onPhotoRemove();
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const triggerCameraInput = () => {
    cameraInputRef.current?.click();
  };

  return (
    <div className="photo-upload">
      {preview ? (
        <div className="photo-preview">
          <img src={preview} alt="Note" className="preview-image" />
          <button
            type="button"
            className="btn-remove-photo"
            onClick={handleRemove}
            title="Remove photo"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="photo-upload-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          <button
            type="button"
            className="btn-upload-option"
            onClick={triggerFileInput}
            disabled={uploading}
          >
            <span className="upload-icon">🖼️</span>
            <span className="upload-text">Choose Photo</span>
          </button>

          <button
            type="button"
            className="btn-upload-option"
            onClick={triggerCameraInput}
            disabled={uploading}
          >
            <span className="upload-icon">📸</span>
            <span className="upload-text">Take Photo</span>
          </button>
        </div>
      )}

      {uploading && (
        <div className="upload-progress">
          <div className="spinner-tiny"></div>
          <span>Compressing image...</span>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
